import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  LegalSection,
  LegalListItem,
  legalH2,
  legalH3,
  legalP,
} from '@/components/legal/LegalLayout';

export function PrivacyPage() {
  return (
    <>
      <Helmet>
        <title>Privacy Policy - CriticalBridge.AI</title>
        <meta
          name="description"
          content="CriticalBridge.AI Privacy Policy - Learn how we collect, use, and protect your personal information."
        />
        <link rel="canonical" href="https://www.criticalbridge.ai/privacy" />
      </Helmet>

      {/* Hero */}
      <section className="bg-secondary">
        <div className="container mx-auto px-4 py-16 sm:py-20">
          <div className="font-mono text-xs text-secondary-foreground/60">
            <Link to="/" className="hover:text-secondary-foreground">
              Home
            </Link>{' '}
            / Privacy Policy
          </div>
          <h1 className="mt-4 text-4xl font-extrabold text-secondary-foreground sm:text-5xl">
            Privacy Policy
          </h1>
          <div className="mt-3 font-mono text-xs text-secondary-foreground/60">
            Last updated: February 25, 2026
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="container mx-auto max-w-[74ch] px-4">
          <LegalSection>
            <h2 className={legalH2}>1. Introduction</h2>
            <p className={legalP}>
              CriticalBridge.AI ("we," "our," or "us") is committed to protecting your privacy. This
              Privacy Policy explains how we collect, use, disclose, and safeguard your information
              when you visit our website, use our desktop application, or use our workflow
              automation services (collectively, the "Services").
            </p>
            <p className={legalP}>
              Please read this Privacy Policy carefully. By accessing or using our Services, you
              acknowledge that you have read, understood, and agree to be bound by this Privacy
              Policy.
            </p>
          </LegalSection>

          <LegalSection>
            <h2 className={legalH2}>2. Information We Collect</h2>

            <h3 className={legalH3}>Personal Information</h3>
            <p className={legalP}>
              We may collect personal information that you voluntarily provide when you:
            </p>
            <ul role="list" className="mt-3 grid gap-2">
              <LegalListItem>Register for an account</LegalListItem>
              <LegalListItem>Subscribe to our newsletter</LegalListItem>
              <LegalListItem>Contact us for support</LegalListItem>
              <LegalListItem>Complete forms on our website</LegalListItem>
              <LegalListItem>Participate in surveys or promotions</LegalListItem>
            </ul>
            <p className={legalP}>
              This information may include your name, email address, phone number, company name, job
              title, billing information, and any other information you choose to provide.
            </p>

            <h3 className={legalH3}>Usage Information</h3>
            <p className={legalP}>
              We automatically collect certain information when you use our Services, including:
            </p>
            <ul role="list" className="mt-3 grid gap-2">
              <LegalListItem>
                Device information (browser type, operating system, device type)
              </LegalListItem>
              <LegalListItem>IP address and location data</LegalListItem>
              <LegalListItem>Pages visited and features used</LegalListItem>
              <LegalListItem>Time spent on pages and click patterns</LegalListItem>
              <LegalListItem>Referring URLs and search terms</LegalListItem>
            </ul>

            <h3 className={legalH3}>Workflow Data</h3>
            <p className={legalP}>
              When you use our workflow automation services, we process the data necessary to
              execute your workflows. This may include data from third-party services you connect to
              CriticalBridge.AI. We process this data solely to provide our Services and do not use
              it for any other purpose.
            </p>
          </LegalSection>

          <LegalSection>
            <h2 className={legalH2}>3. How We Use Your Information</h2>
            <p className={legalP}>We use the information we collect to:</p>
            <ul role="list" className="mt-3 grid gap-2">
              <LegalListItem>Provide, operate, and maintain our Services</LegalListItem>
              <LegalListItem>Process transactions and send related information</LegalListItem>
              <LegalListItem>
                Send administrative information, updates, and security alerts
              </LegalListItem>
              <LegalListItem>
                Respond to your comments, questions, and support requests
              </LegalListItem>
              <LegalListItem>Analyze usage patterns to improve our Services</LegalListItem>
              <LegalListItem>Develop new products, services, and features</LegalListItem>
              <LegalListItem>Send marketing communications (with your consent)</LegalListItem>
              <LegalListItem>Detect, prevent, and address technical issues and fraud</LegalListItem>
              <LegalListItem>Comply with legal obligations</LegalListItem>
            </ul>
          </LegalSection>

          <LegalSection>
            <h2 className={legalH2}>4. How We Share Your Information</h2>
            <p className={legalP}>We may share your information in the following circumstances:</p>

            <h3 className={legalH3}>Service Providers</h3>
            <p className={legalP}>
              We share information with third-party vendors who perform services on our behalf, such
              as hosting, analytics, payment processing, and customer support. These providers are
              contractually obligated to protect your information.
            </p>

            <h3 className={legalH3}>Business Transfers</h3>
            <p className={legalP}>
              If we are involved in a merger, acquisition, or sale of assets, your information may
              be transferred as part of that transaction. We will notify you of any change in
              ownership or use of your personal information.
            </p>

            <h3 className={legalH3}>Legal Requirements</h3>
            <p className={legalP}>
              We may disclose your information if required by law or in response to valid requests
              by public authorities (e.g., court orders, government requests).
            </p>
          </LegalSection>

          <LegalSection>
            <h2 className={legalH2}>5. Cookies and Tracking Technologies</h2>
            <p className={legalP}>
              We use cookies and similar tracking technologies to collect and track information
              about your activity on our Services. Cookies are small data files stored on your
              device that help us improve your experience.
            </p>
            <p className={legalP}>Types of cookies we use:</p>
            <ul role="list" className="mt-3 grid gap-2">
              <LegalListItem>
                <strong className="text-foreground">Essential Cookies:</strong> Required for the
                Services to function properly
              </LegalListItem>
              <LegalListItem>
                <strong className="text-foreground">Analytics Cookies:</strong> Help us understand
                how visitors use our Services
              </LegalListItem>
              <LegalListItem>
                <strong className="text-foreground">Preference Cookies:</strong> Remember your
                settings and preferences
              </LegalListItem>
              <LegalListItem>
                <strong className="text-foreground">Marketing Cookies:</strong> Track your activity
                for advertising purposes
              </LegalListItem>
            </ul>
            <p className={legalP}>
              You can control cookies through your browser settings. However, disabling certain
              cookies may limit your ability to use some features of our Services.
            </p>
          </LegalSection>

          <LegalSection>
            <h2 className={legalH2}>6. Data Retention</h2>
            <p className={legalP}>
              We retain your personal information for as long as necessary to fulfill the purposes
              outlined in this Privacy Policy, unless a longer retention period is required or
              permitted by law. When we no longer need your information, we will securely delete or
              anonymize it. Workflow execution data is retained according to your account settings
              and our data retention policies.
            </p>
          </LegalSection>

          <LegalSection>
            <h2 className={legalH2}>7. Data Security</h2>
            <p className={legalP}>
              We implement appropriate technical and organizational security measures to protect
              your personal information against unauthorized access, alteration, disclosure, or
              destruction. These measures include encryption, access controls, secure data centers,
              and regular security assessments. However, no method of transmission over the Internet
              or electronic storage is 100% secure, and we cannot guarantee absolute security.
            </p>
          </LegalSection>

          <LegalSection>
            <h2 className={legalH2}>8. Your Rights and Choices</h2>
            <p className={legalP}>
              Depending on your location, you may have certain rights regarding your personal
              information:
            </p>
            <ul role="list" className="mt-3 grid gap-2">
              <LegalListItem>
                <strong className="text-foreground">Access:</strong> Request a copy of your personal
                information
              </LegalListItem>
              <LegalListItem>
                <strong className="text-foreground">Correction:</strong> Request correction of
                inaccurate information
              </LegalListItem>
              <LegalListItem>
                <strong className="text-foreground">Deletion:</strong> Request deletion of your
                personal information
              </LegalListItem>
              <LegalListItem>
                <strong className="text-foreground">Portability:</strong> Request your data in a
                portable format
              </LegalListItem>
              <LegalListItem>
                <strong className="text-foreground">Opt-out:</strong> Opt out of marketing
                communications
              </LegalListItem>
              <LegalListItem>
                <strong className="text-foreground">Restriction:</strong> Request restriction of
                processing
              </LegalListItem>
              <LegalListItem>
                <strong className="text-foreground">Objection:</strong> Object to certain processing
                activities
              </LegalListItem>
            </ul>
            <p className={legalP}>
              To exercise these rights, please contact us at privacy@criticalbridge.ai. We will
              respond to your request within the timeframe required by applicable law.
            </p>
          </LegalSection>

          <LegalSection>
            <h2 className={legalH2}>9. European Users (GDPR)</h2>
            <p className={legalP}>
              If you are located in the European Economic Area (EEA), United Kingdom, or
              Switzerland, you have additional rights under the General Data Protection Regulation
              (GDPR). We process your personal data based on legitimate interests, contract
              performance, legal obligations, or your consent. You have the right to lodge a
              complaint with your local data protection authority if you believe we have violated
              your privacy rights.
            </p>
          </LegalSection>

          <LegalSection>
            <h2 className={legalH2}>10. California Users (CCPA)</h2>
            <p className={legalP}>
              If you are a California resident, the California Consumer Privacy Act (CCPA) provides
              you with additional rights, including:
            </p>
            <ul role="list" className="mt-3 grid gap-2">
              <LegalListItem>
                The right to know what personal information we collect and how it is used
              </LegalListItem>
              <LegalListItem>The right to delete your personal information</LegalListItem>
              <LegalListItem>
                The right to opt out of the sale of your personal information
              </LegalListItem>
              <LegalListItem>
                The right to non-discrimination for exercising your privacy rights
              </LegalListItem>
            </ul>
            <p className={legalP}>
              We do not sell your personal information. To exercise your CCPA rights, please contact
              us at privacy@criticalbridge.ai.
            </p>
          </LegalSection>

          <LegalSection>
            <h2 className={legalH2}>11. Children's Privacy</h2>
            <p className={legalP}>
              Our Services are not intended for children under the age of 16. We do not knowingly
              collect personal information from children under 16. If we become aware that we have
              collected personal information from a child under 16, we will take steps to delete
              that information promptly.
            </p>
          </LegalSection>

          <LegalSection>
            <h2 className={legalH2}>12. International Data Transfers</h2>
            <p className={legalP}>
              Your information may be transferred to and processed in countries other than your
              country of residence. These countries may have different data protection laws. When we
              transfer your information internationally, we implement appropriate safeguards to
              ensure your information remains protected, including standard contractual clauses
              approved by relevant authorities.
            </p>
          </LegalSection>

          <LegalSection>
            <h2 className={legalH2}>13. Changes to This Privacy Policy</h2>
            <p className={legalP}>
              We may update this Privacy Policy from time to time. We will notify you of any
              material changes by posting the new Privacy Policy on this page and updating the "Last
              updated" date. We encourage you to review this Privacy Policy periodically for any
              changes.
            </p>
          </LegalSection>

          <LegalSection>
            <h2 className={legalH2}>14. Contact Us</h2>
            <p className={legalP}>
              If you have any questions about this Privacy Policy or our privacy practices, please
              contact us:
            </p>
            <div className="mt-4 rounded-lg border bg-card p-5">
              <p className="font-bold">CriticalBridge.AI</p>
              <p className="mt-1 text-sm text-muted-foreground">Email: privacy@criticalbridge.ai</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Address: 123 Innovation Drive, San Francisco, CA 94105
              </p>
              <p className="mt-1 text-sm text-muted-foreground">Phone: +1 (555) 123-4567</p>
            </div>
          </LegalSection>
        </div>
      </section>
    </>
  );
}
